import posthog from 'posthog-js';

// Google Analytics Configuration
export const GA_TRACKING_ID = 'G-4NR814VNYK';
export const GOOGLE_ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID || '';

/**
 * Centralised event names so the helpers below and any GA4 / Google Ads
 * conversion mappings agree on the spelling. These are the events worth
 * marking as Key Events in GA4 (and importing as conversions in Ads).
 */
export const KEY_EVENTS = {
  SIGN_UP: 'sign_up',
  BEGIN_CHECKOUT: 'begin_checkout',
  PURCHASE: 'purchase',
  APP_INSTALL_CLICK: 'app_install_click',
  AFFILIATE_SIGNUP_COMPLETE: 'affiliate_signup_complete',
  COACH_INQUIRY: 'coach_inquiry',
  MEAL_PLAN_CREATED: 'meal_plan_created',
  INSTACART_CHECKOUT_CLICK: 'instacart_checkout_click',
  WORKOUT_STARTED: 'workout_started',
};

const isBot = () => {
  if (typeof navigator === 'undefined') return true;
  // react-snap injects this on the window during build-time prerender; we
  // never want analytics to fire during snapshotting.
  if (typeof window !== 'undefined' && window.__PRERENDER_INJECTED) return true;
  if (typeof navigator.webdriver !== 'undefined' && navigator.webdriver) return true;
  return /bot|crawl|spider|slurp|facebookexternalhit|bingpreview|google-inspectiontool|lighthouse|pagespeed|headlesschrome|headless|phantomjs|prerender|reactsnap|react-snap|wget|curl|python-requests|go-http-client|node-fetch|uptimerobot/i.test(
    navigator.userAgent
  );
};

// ── UTM Capture ──────────────────────────────────────────────
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

export const captureUTM = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    let hasUTM = false;
    UTM_PARAMS.forEach((key) => {
      const val = params.get(key);
      if (val) {
        utm[key] = val;
        hasUTM = true;
      }
    });
    // Also persist gclid / gbraid / wbraid for Google Ads attribution.
    ['gclid', 'gbraid', 'wbraid'].forEach((key) => {
      const val = params.get(key);
      if (val) {
        utm[key] = val;
        hasUTM = true;
      }
    });
    if (hasUTM) {
      utm.landing_url = window.location.pathname;
      utm.timestamp = new Date().toISOString();
      sessionStorage.setItem('utm_data', JSON.stringify(utm));
    }
  } catch (e) {
    // sessionStorage may be unavailable in some contexts
  }
};

export const getUTMData = () => {
  try {
    const raw = sessionStorage.getItem('utm_data');
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

// ── PostHog Initialization ───────────────────────────────────
export const initPostHog = () => {
  if (typeof window === 'undefined' || isBot()) return;
  const key = process.env.REACT_APP_POSTHOG_KEY;
  if (!key) return;

  posthog.init(key, {
    api_host: '/ingest',
    ui_host: 'https://us.posthog.com',
    capture_pageview: false, // we handle this via RouteTracker
    capture_pageleave: true,
    autocapture: true,
    persistence: 'localStorage+cookie',
  });
};

// ── Initialize Google Analytics ──────────────────────────────
export const initGA = () => {
  if (typeof window === 'undefined' || isBot()) return;

  const script1 = document.createElement('script');
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
  document.head.appendChild(script1);

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  gtag('js', new Date());
  gtag('config', GA_TRACKING_ID, {
    send_page_view: false,
    // Linker keeps gclid + UTM data flowing across subdomains and the Stripe redirect.
    linker: { domains: ['freshfitlab.io', 'www.freshfitlab.io', 'freshmenulab.com', 'www.freshmenulab.com'] },
  });

  window.gtag = gtag;
};

// ── Initialize Google Ads ────────────────────────────────────
export const initGoogleAds = () => {
  if (typeof window === 'undefined' || isBot()) return;
  if (!GOOGLE_ADS_ID) return; // Only loads when an ID is configured.

  // gtag.js is shared with GA4, so we don't need to load the script again
  // if initGA already ran. We just push another `config` for the Ads ID.
  if (typeof window.gtag !== 'function') {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
    document.head.appendChild(script);
    window.gtag('js', new Date());
  }

  window.gtag('config', GOOGLE_ADS_ID, {
    allow_enhanced_conversions: true,
  });
};

const sendGtagEvent = (action, params = {}) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', action, params);
  }
};

const sendAdsConversion = (envKey, value, currency = 'USD') => {
  if (!GOOGLE_ADS_ID) return;
  const sendTo = process.env[envKey];
  if (!sendTo) return;
  if (typeof window.gtag !== 'function') return;
  const payload = { send_to: sendTo };
  if (typeof value === 'number') payload.value = value;
  if (currency) payload.currency = currency;
  window.gtag('event', 'conversion', payload);
};

// ── Page Views (GA4 + PostHog) ───────────────────────────────
export const pageview = (url) => {
  if (isBot()) return;
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_path: url,
      page_location: window.location.href,
    });
  }
  try {
    posthog.capture('$pageview', { path: url });
  } catch {
    // posthog may not be initialized yet (prerender / missing key)
  }
};

const phCapture = (event, props, options) => {
  try {
    posthog.capture(event, props, options);
  } catch {
    // ignore: not initialized
  }
};

// ── Generic Events (GA4 + PostHog) ──────────────────────────
export const event = ({ action, category, label, value }) => {
  sendGtagEvent(action, {
    event_category: category,
    event_label: label,
    value: value,
  });
  phCapture(action, { category, label, value });
};

// ── Conversion Funnel Events ─────────────────────────────────
export const trackLandingView = (persona) => {
  const utm = getUTMData();
  const props = { persona, ...utm };
  sendGtagEvent('landing_page_view', {
    event_category: 'LandingPage',
    event_label: persona,
    ...utm,
  });
  phCapture('landing_page_view', props);
};

export const trackCTAClick = (persona, ctaLocation) => {
  const utm = getUTMData();
  const props = { persona, cta_location: ctaLocation, ...utm };
  sendGtagEvent('cta_click', {
    event_category: 'LandingPage',
    event_label: `${persona}_${ctaLocation}`,
    ...utm,
  });
  phCapture('cta_click', props);
};

export const trackSignupStart = (persona, method = 'google') => {
  const utm = getUTMData();
  const props = { persona, method, ...utm };
  sendGtagEvent('signup_start', {
    event_category: 'Conversion',
    event_label: persona,
    method,
    ...utm,
  });
  phCapture('signup_start', props);
};

export const trackSignupComplete = (persona, userId) => {
  const utm = getUTMData();
  const props = { persona, ...utm };
  sendGtagEvent(KEY_EVENTS.SIGN_UP, {
    event_category: 'Conversion',
    event_label: persona,
    method: 'google',
    // The auth handlers reload the page right after this call. Beacon
    // transport survives the navigation; a normal request gets cancelled,
    // which is why sign_up barely registered despite regular signups.
    transport_type: 'beacon',
    ...utm,
  });
  sendAdsConversion('REACT_APP_GOOGLE_ADS_CONVERSION_SIGNUP');
  phCapture('signup_complete', props, { transport: 'sendBeacon' });

  if (userId) {
    try {
      posthog.identify(userId);
    } catch {
      // ignore
    }
  }
};

/**
 * Returning-user login. Separate from trackSignupComplete so `sign_up`
 * stays a true new-account conversion — the backend now returns
 * `isNewUser` from /auth/google and /auth/apple to branch on. Beacon
 * transport for the same page-reload reason as above.
 */
export const trackLogin = (source, userId) => {
  sendGtagEvent('login', {
    event_category: 'Auth',
    event_label: source,
    method: 'google',
    transport_type: 'beacon',
  });
  phCapture('login', { source }, { transport: 'sendBeacon' });

  if (userId) {
    try {
      posthog.identify(userId);
    } catch {
      // ignore
    }
  }
};

/**
 * A free user collided with a plan limit or saw an upgrade nudge. This is
 * the top of the free→paid funnel — fire on impression (shown) and on
 * click-through so upgrade_prompt_shown → upgrade_prompt_click →
 * begin_checkout → purchase is measurable end to end.
 * `feature` values in use: ai_coach_limit, ai_coach_nudge, menu_limit,
 * menu_limit_nudge, trial_banner, program_import.
 */
export const trackUpgradePromptShown = (feature, props = {}) => {
  sendGtagEvent('upgrade_prompt_shown', {
    event_category: 'Subscription',
    event_label: feature,
    feature,
    ...props,
  });
  phCapture('upgrade_prompt_shown', { feature, ...props });
};

export const trackUpgradePromptClick = (feature, props = {}) => {
  sendGtagEvent('upgrade_prompt_click', {
    event_category: 'Subscription',
    event_label: feature,
    feature,
    ...props,
  });
  phCapture('upgrade_prompt_click', { feature, ...props });
};

export const trackSubscriptionStart = (plan) => {
  const utm = getUTMData();
  sendGtagEvent(KEY_EVENTS.BEGIN_CHECKOUT, {
    event_category: 'Subscription',
    event_label: plan,
    ...utm,
  });
  sendAdsConversion('REACT_APP_GOOGLE_ADS_CONVERSION_BEGIN_CHECKOUT');
  phCapture('subscription_start', { plan, ...utm });
};

export const trackSubscriptionComplete = (plan, value, currency = 'USD') => {
  const utm = getUTMData();
  sendGtagEvent(KEY_EVENTS.PURCHASE, {
    event_category: 'Subscription',
    event_label: plan,
    transaction_id: utm.gclid || `sub_${Date.now()}`,
    value,
    currency,
    items: [
      {
        item_id: plan,
        item_name: `Subscription - ${plan}`,
        item_category: 'subscription',
        price: value,
        quantity: 1,
      },
    ],
    ...utm,
  });
  sendAdsConversion('REACT_APP_GOOGLE_ADS_CONVERSION_PURCHASE', value, currency);
  phCapture('subscription_complete', { plan, value, currency, ...utm });
};

// ── Engagement / secondary conversions ───────────────────────
export const trackAppInstallClick = (platform, location) => {
  const utm = getUTMData();
  sendGtagEvent(KEY_EVENTS.APP_INSTALL_CLICK, {
    event_category: 'AppInstall',
    event_label: `${platform}_${location || 'unknown'}`,
    platform,
    location,
    ...utm,
  });
  sendAdsConversion('REACT_APP_GOOGLE_ADS_CONVERSION_APP_INSTALL');
  phCapture('app_install_click', { platform, location, ...utm });
};

export const trackAffiliateSignupComplete = (specialization) => {
  const utm = getUTMData();
  sendGtagEvent(KEY_EVENTS.AFFILIATE_SIGNUP_COMPLETE, {
    event_category: 'Conversion',
    event_label: specialization || 'general',
    ...utm,
  });
  sendAdsConversion('REACT_APP_GOOGLE_ADS_CONVERSION_AFFILIATE_SIGNUP');
  phCapture('affiliate_signup_complete', { specialization, ...utm });
};

export const trackCoachInquiry = (coachId, action) => {
  sendGtagEvent(KEY_EVENTS.COACH_INQUIRY, {
    event_category: 'Coach',
    event_label: action,
    coach_id: coachId,
  });
  phCapture('coach_inquiry', { coachId, action });
};

export const trackMealPlanCreated = (source) => {
  sendGtagEvent(KEY_EVENTS.MEAL_PLAN_CREATED, {
    event_category: 'Engagement',
    event_label: source,
  });
  phCapture('meal_plan_created', { source });
};

export const trackInstacartCheckoutClick = (itemCount, source) => {
  sendGtagEvent(KEY_EVENTS.INSTACART_CHECKOUT_CLICK, {
    event_category: 'Engagement',
    event_label: source,
    items: itemCount,
  });
  sendAdsConversion('REACT_APP_GOOGLE_ADS_CONVERSION_INSTACART');
  phCapture('instacart_checkout_click', { itemCount, source });
};

export const trackWorkoutStarted = (workoutId, programId) => {
  sendGtagEvent(KEY_EVENTS.WORKOUT_STARTED, {
    event_category: 'Engagement',
    event_label: programId || 'standalone',
    workout_id: workoutId,
    program_id: programId,
  });
  phCapture('workout_started', { workoutId, programId });
};

// ── User Identity ────────────────────────────────────────────
export const identifyUser = (userId, traits = {}) => {
  try {
    posthog.identify(userId, traits);
  } catch {
    // ignore
  }
  if (typeof window.gtag === 'function') {
    window.gtag('set', { user_id: userId });
  }
};
